import React from "react";
import { useTranslation } from "react-i18next";
import { Cart } from "../../types/PricingCalculator";

interface UserCountStepProps {
  cart: Cart;
  onUpdate: (updates: Partial<Cart>) => void;
}

const UserCountStep: React.FC<UserCountStepProps> = ({ cart, onUpdate }) => {
  const { t } = useTranslation();
  const handleUserCountChange = (count: number) => {
    onUpdate({
      userCount: count,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
          {t("pricingCalc.userCountStep.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t("pricingCalc.userCountStep.subtitle")}
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {t("pricingCalc.userCountStep.additionalUsers")}
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {t("pricingCalc.userCountStep.freeUsers")}.{" "}
                {t("pricingCalc.userCountStep.additionalUsers")} cost 5,000 RWF{" "}
                {t("pricingCalc.userCountStep.perUser")}.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="user-count"
            className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
          >
            {t("pricingCalc.userCountStep.userCount")}
          </label>
          <input
            type="number"
            id="user-count"
            min="1"
            max="50"
            value={cart.userCount}
            onChange={(e) =>
              handleUserCountChange(parseInt(e.target.value) || 1)
            }
            className="w-full px-3 py-2 border dark:text-gray-900 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {cart.userCount > 2 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.726-1.36 3.491 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">
                  {t("pricingCalc.userCountStep.additionalUsers")}
                </h4>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    You have {cart.userCount - 2} additional user(s) beyond the
                    included 2 users. This will add{" "}
                    {(cart.userCount - 2) * 5000} RWF per month to your software
                    cost.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCountStep;
